import Card from "@/components/common/Card";
import MainHeading from "@/components/common/MainHeading";
import Layout from "@/components/layout/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import React, { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { callApi, ThrowError } from "@/api/callApi";
import { showToastNotification } from "@/utility/helper";
import { RoutesLink } from "@/utility/RouteLinks";
import EmployeeOnBoardService from "@/services/EmployeeOnBoard/EmployeeOnBoardService";
import type { EmployeeCategory, EmployeeOnBoard, IEmployee, marrideStatus } from "@/interface/employeeOnBoard";
import { API_ENDPOINT } from "@/api/constant";
import { ChevronLeft, ChevronRight, Check, ShieldCheck, Lock } from "lucide-react";
import { useAppSelector } from "@/store/store";
import OnboardBasicDetail from "../Employee/EmployeeOnboard/OnboardBasicDetail";
import OnboardPermissions from "../Employee/EmployeeOnboard/OnboardPermissions";
import OnboardClient from "../Employee/EmployeeOnboard/OnboardClient";
import OnboardAdvanceDetails from "../Employee/EmployeeOnboard/OnboardAdvanceDetails";
import OnboardAddress from "../Employee/EmployeeOnboard/OnboardAddress";
import OnboardBankDetails from "../Employee/EmployeeOnboard/OnboardBankDetails";
import OnboardNominee from "../Employee/EmployeeOnboard/OnboardNominee";
import OnboardDocuments from "../Employee/EmployeeOnboard/OnboardDocuments";
import OnboardSalaryDetail from "../Employee/EmployeeOnboard/OnboardSalaryDetail";
import { useOnboardProgress } from "../Employee/EmployeeOnboard/useOnboardProgress";
import { ONBOARD_STEPS } from "../Employee/EmployeeOnboard/onboardConfig";
import type { CustomDocumentPayload } from "../Employee/EmployeeOnboard/onboard.types";

// ─── Tab Content Mapping ────────────────────────────────────────────────────
// Maps each tabKey to its React component for clean rendering
const TAB_CONTENT_MAP: Record<string, React.FC<any>> = {
    tab1: OnboardBasicDetail,
    tab2: OnboardPermissions,
    tab3: OnboardClient,
    tab4: OnboardAdvanceDetails,
    tab5: OnboardAddress,
    tab6: OnboardBankDetails,
    tab7: OnboardNominee,
    tab8: OnboardDocuments,
    tab9: OnboardSalaryDetail,
};

// ─── Shared tab trigger class ───────────────────────────────────────────────
const TAB_TRIGGER_CLASS =
    "rounded-lg py-3.5 px-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary data-[state=active]:border-primary/20 border border-transparent flex-1 font-semibold transition-all flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 data-[state=active]:hover:bg-white";

// ─── Component ──────────────────────────────────────────────────────────────
const OnBoard: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isProfile] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("tab1");
    const [isVerified, setIsVerified] = useState(false);
    const [isGuestOrLevel5, setIsGuestOrLevel5] = useState(false);

    // ── Auth ─────────────────────────────────────────────────────────────
    const loggedInUser = useAppSelector((state) => state.user.user);
    const loggedInUserId = loggedInUser?.id || "";
    const currentId = id || loggedInUserId;

    // ── Filter steps dynamically to exclude Permissions (tab2), Client (tab3), and Salary Detail (tab9) ──
    const filteredSteps = ONBOARD_STEPS.filter((step) => {
        return step.tabKey !== "tab2" && step.tabKey !== "tab3" && step.tabKey !== "tab9";
    });

    // ── Progress tracking via onboardingProcess ──────────────────────────
    const progress = useOnboardProgress(filteredSteps.length);

    // ── Scrollable tabs ──────────────────────────────────────────────────
    const tabsRef = React.useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);

    const checkScrollButtons = () => {
        if (tabsRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current;
            setShowLeftArrow(scrollLeft > 10);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    const scrollTabs = (direction: "left" | "right") => {
        if (tabsRef.current) {
            const scrollAmount = direction === "left" ? -250 : 250;
            tabsRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
        }
    };

    useEffect(() => {
        checkScrollButtons();
        window.addEventListener("resize", checkScrollButtons);
        const timer = setTimeout(() => {
            if (tabsRef.current) {
                const activeTabElement = tabsRef.current.querySelector(`[data-state="active"]`);
                if (activeTabElement) {
                    activeTabElement.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
                }
                checkScrollButtons();
            }
        }, 50);
        return () => {
            window.removeEventListener("resize", checkScrollButtons);
            clearTimeout(timer);
        };
    }, [activeTab]);


    // ── Helpers ──────────────────────────────────────────────────────────
    const formatDateForInput = (value?: string | null) =>
        value ? value.split("T")[0] : "";

    const normalizeEmployeeForForm = (employee: IEmployee): EmployeeOnBoard => {
        const userDetail = employee.userDetail;

        return {
            name: employee.name || "",
            email: employee.email?.toLowerCase()?.trim() || "",
            phone: employee.phone || "",
            secondaryPhone: employee.secondaryPhone || "",
            employeeCode: employee.employeeCode || "",
            type: String(employee.type ?? "") as EmployeeOnBoard["type"],
            gender: String(employee.gender ?? "") as EmployeeOnBoard["gender"],
            category: (employee.category as EmployeeCategory) || 'Trainee',
            status: (employee.status || "") as EmployeeOnBoard["status"],
            dob: formatDateForInput(employee.dob),
            joiningDate: formatDateForInput(employee.joiningDate),
            personalEmail: employee.personalEmail || "",
            onboardingProcess: employee.onboardingProcess || [],
            bloodGroup: employee.bloodGroup || "",
            profilePhoto: employee.profilePhoto || "",
            isMarried: (employee.isMarried as marrideStatus) || "",
            spouse: employee.spouse || userDetail?.spouse || "",
            children: employee.children ?? userDetail?.children ?? "",
            skillType: employee.skillType || "",
            permissions: Array.isArray(employee.permissions)
                ? employee.permissions.join(",")
                : employee.permissions || "",
            isAssignedAllSite: employee.isAssignedAllSite ?? false,
            assignedSiteType: employee.assignedSiteType || "",
            departmentId: employee.departmentId || "",
            designationId: employee.designationId || "",
            shiftId: employee.shiftId || "",
            siteId: employee.siteId || "",
            branchId: employee.branchId || "",
            fatherName: userDetail?.fatherName || "",
            motherName: userDetail?.motherName || "",
            uanNumber: userDetail?.uanNumber || "",
            aadharNumber: userDetail?.aadharNumber || "",
            panFront: userDetail?.panFront || "",
            aadharFront: userDetail?.aadharFront || "",
            passport: userDetail?.passport || "",
            esicNumber: userDetail?.esicNumber || "",
            insuranceNumber: userDetail?.insuranceNumber || "",
            pfNumber: userDetail?.pfNumber || "",
            panNumber: userDetail?.panNumber || "",
            drivingLicenseNumber: userDetail?.drivingLicenseNumber || "",
            address: userDetail?.address || "",
            secondaryAddress: userDetail?.secondaryAddress || "",
            state: userDetail?.state || "",
            city: userDetail?.city || "",
            pinCode: userDetail?.pinCode || "",
            permanentAddress: userDetail?.permanentAddress || "",
            isSameAsPermanentAddress: userDetail?.isSameAsPermanentAddress ?? false,
            secondaryPermanentAddress: userDetail?.secondaryPermanentAddress || "",
            permanentState: userDetail?.permanentState || "",
            permanentCity: userDetail?.permanentCity || "",
            permanentPinCode: userDetail?.permanentPinCode || "",
            bankName: userDetail?.bankName || "",
            ifscCode: userDetail?.ifscCode || "",
            accountHolderName: userDetail?.accountHolderName || "",
            accountNumber: userDetail?.accountNumber || "",
            branchAddress: userDetail?.branchAddress || "",
            nomineeName: userDetail?.nomineeName || "",
            nomineePhone: userDetail?.nomineePhone || "",
            nomineeDob: formatDateForInput(userDetail?.nomineeDob),
            nomineeRelation: userDetail?.nomineeRelation || "",
            nomineeEmail: userDetail?.nomineeEmail || "",
            nomineeAddress: userDetail?.nomineeAddress || "",
            nomineePercentage: userDetail?.nomineePercentage ?? "",
            ecpName: userDetail?.ecpName || "",
            ecpPhone: userDetail?.ecpPhone || "",
            ecpRelation: userDetail?.ecpRelation || "",
            dailyWages: userDetail?.dailyWages ?? "",
            grossSalary: userDetail?.grossSalary ?? "",
            hra: userDetail?.hra ?? "",
            otherAllowances: userDetail?.otherAllowances ?? "",
            user: { id: employee.id },
            userDetail: userDetail || undefined,
            oauthClients: employee.oauthClients || [],
            campusId: employee.campusId || "",
            clientIds: (employee.oauthClients || [])
                .map((oc) => oc.id)
                .filter(Boolean) as string[],
            teamLeadId: employee.teamLead?.id,

        };
    };

    // ── Form ─────────────────────────────────────────────────────────────
    const methods = useForm<EmployeeOnBoard>({
        mode: "onChange",
        reValidateMode: "onChange",
    });



    // ── Fetch employee data (edit mode) ──────────────────────────────────
    useEffect(() => {
        const fetchId = id || loggedInUserId;
        if (!fetchId) return;
        const fetchEmployee = async () => {
            setIsLoading(true);
            try {
                const response = await EmployeeOnBoardService.getEmployeeById(fetchId);
                if (response?.status) {
                    const normalized = normalizeEmployeeForForm(response.data);

                    // Fetch documents from the dedicated API
                    try {
                        const docsResponse = await EmployeeOnBoardService.getEmployeeDocuments(fetchId);
                        if (docsResponse?.status && docsResponse?.data) {
                            const docList = Array.isArray(docsResponse.data)
                                ? docsResponse.data
                                : docsResponse.data.documents || [];

                            docList.forEach((doc: any) => {
                                if (doc.name === "Aadhar Card Front" || doc.type === "AADHAR_FRONT") normalized.aadharFront = doc.url;
                                if (doc.name === "Aadhar Card Back" || doc.type === "AADHAR_BACK") normalized.aadharBack = doc.url;
                                if (doc.name === "PAN Card" || doc.type === "PAN_CARD") normalized.panFront = doc.url;
                                if (doc.name === "Driving License" || doc.type === "DRIVING_LICENSE") normalized.drivingLicense = doc.url;
                                if (doc.name === "Passport" || doc.type === "PASSPORT") normalized.passport = doc.url;
                                if (doc.name === "Marriage Certificate") normalized.marriageCertificate = doc.url;
                                if (doc.name === "Current Address Proof") normalized.currentAddressProof = doc.url;
                                if (doc.name === "Permanent Address Proof") normalized.permanentAddressProof = doc.url;
                                if (doc.name === "SSC Marksheet" || doc.name === "SSC") normalized.sscDoc = doc.url;
                                if (doc.name === "HSC Marksheet" || doc.name === "HSC") normalized.hscDoc = doc.url;
                                if (doc.name === "Graduation Marksheet" || doc.name === "Graduation") normalized.graduationDoc = doc.url;
                                if (doc.name === "Post Graduation Marksheet" || doc.name === "Post Graduation") normalized.postGraduationDoc = doc.url;
                                if (doc.name === "Masters Marksheet" || doc.name === "Masters") normalized.mastersDoc = doc.url;
                            });
                        }
                    } catch (docError) {
                        console.error("Error fetching employee documents:", docError);
                    }

                    normalized.isTeamLead =
                        loggedInUser?.teamLead?.id === response.data.id;

                    methods.reset(normalized);

                    // Check designation criteria (level 5 or Guest Employee)
                    const designationLevel = Number(response.data.designation?.level);
                    const designationName = (response.data.designation?.name || response.data.designationName || "").toLowerCase();
                    const isGuest = designationLevel === 5 || designationName.includes("guest employee");
                    if (isGuest) {
                        setIsGuestOrLevel5(true);
                    }

                    // Seed progress from server, filtering out hidden steps if in guest mode
                    const serverSteps = response.data.onboardingProcess || [];
                    const validSteps = ONBOARD_STEPS.filter(step => {
                        if (isGuest) {
                            return step.tabKey !== "tab2" && step.tabKey !== "tab3" && step.tabKey !== "tab9";
                        }
                        return true;
                    });
                    const validStepIds = validSteps.map(s => s.stepId);
                    const filteredServerSteps = serverSteps.filter(stepId => validStepIds.includes(stepId));
                    progress.initFromServer(filteredServerSteps);

                    // Seed verification status
                    if (response.data.isVerified) {
                        setIsVerified(true);
                    }
                }
            } catch (error) {
                ThrowError(error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchEmployee();
    }, [id, loggedInUserId, methods]);

    // ── File upload helper ───────────────────────────────────────────────
    const uploadFile = async (file: File): Promise<string | null> => {
        const formData = new FormData();
        formData.append("file", file);
        try {
            const response = await callApi(`${API_ENDPOINT.fileUpload}/upload`, {
                method: "POST",
                data: formData,
                multipart: true,
            });
            if (response?.status) {
                return response.data.path;
            }
            return null;
        } catch (error) {
            ThrowError(error);
            return null;
        }
    };

    // ── Core submit: create or update employee ──────────────────────────
    const onStepSubmit = async (
        data: Partial<EmployeeOnBoard>,
        stepId: string,
        nextTab?: string,
        customDocuments?: CustomDocumentPayload[],
    ) => {
        setIsLoading(true);
        try {
            // Build the new onboardingProcess array with this step
            const updatedProcess = progress.completedSteps.includes(stepId)
                ? [...progress.completedSteps]
                : [...progress.completedSteps, stepId];

            // Attach onboardingProcess to the payload
            const payload = { ...data, onboardingProcess: updatedProcess };

            if (currentId) {
                if (stepId === "documents") {
                    const documentsArray: { type: string; url: string; name: string; keyName?: string }[] = [];
                    if (data.aadharFront) {
                        documentsArray.push({ type: "PERSONAL", url: data.aadharFront as string, name: "Aadhar Card Front", keyName: "aadharFront" });
                    }
                    if (data.aadharBack) {
                        documentsArray.push({ type: "PERSONAL", url: data.aadharBack as string, name: "Aadhar Card Back", keyName: "aadharBack" });
                    }
                    if (data.panFront) {
                        documentsArray.push({ type: "PERSONAL", url: data.panFront as string, name: "PAN Card", keyName: "panFront" });
                    }
                    if (data.drivingLicense) {
                        documentsArray.push({ type: "PERSONAL", url: data.drivingLicense as string, name: "Driving License", keyName: "drivingLicense" });
                    }
                    if (data.passport) {
                        documentsArray.push({ type: "PERSONAL", url: data.passport as string, name: "Passport", keyName: "passport" });
                    }
                    if (data.marriageCertificate) {
                        documentsArray.push({ type: "PERSONAL", url: data.marriageCertificate as string, name: "Marriage Certificate", keyName: "marriageCertificate" });
                    }
                    if (data.currentAddressProof) {
                        documentsArray.push({ type: "PERSONAL", url: data.currentAddressProof as string, name: "Current Address Proof", keyName: "currentAddressProof" });
                    }
                    if (data.permanentAddressProof) {
                        documentsArray.push({ type: "PERSONAL", url: data.permanentAddressProof as string, name: "Permanent Address Proof", keyName: "permanentAddressProof" });
                    }
                    if (data.sscDoc) {
                        documentsArray.push({ type: "EDUCATION", url: data.sscDoc as string, name: "SSC Marksheet", keyName: "sscDoc" });
                    }
                    if (data.hscDoc) {
                        documentsArray.push({ type: "EDUCATION", url: data.hscDoc as string, name: "HSC Marksheet", keyName: "hscDoc" });
                    }
                    if (data.graduationDoc) {
                        documentsArray.push({ type: "EDUCATION", url: data.graduationDoc as string, name: "Graduation Marksheet", keyName: "graduationDoc" });
                    }
                    if (data.postGraduationDoc) {
                        documentsArray.push({ type: "EDUCATION", url: data.postGraduationDoc as string, name: "Post Graduation Marksheet", keyName: "postGraduationDoc" });
                    }
                    if (data.mastersDoc) {
                        documentsArray.push({ type: "EDUCATION", url: data.mastersDoc as string, name: "Masters Marksheet", keyName: "mastersDoc" });
                    }

                    // ── Custom documents (PERSONAL / EDUCATION / OTHER) ───
                    if (customDocuments && customDocuments.length > 0) {
                        customDocuments.forEach((doc) => {
                            if (doc.file && doc.title) {
                                documentsArray.push({
                                    type: doc.type,
                                    url: doc.file,
                                    name: doc.title,
                                    keyName: doc?.keyName || `${doc.title.trim()}-other`
                                });
                            }
                        });
                    }

                    const response = await EmployeeOnBoardService.saveEmployeeDocuments(
                        currentId,
                        { documents: documentsArray }
                    );
                    if (response?.status) {
                        // Also persist onboardingProcess to editEmployee
                        await EmployeeOnBoardService.editEmployee(
                            { onboardingProcess: updatedProcess },
                            currentId
                        );
                        showToastNotification("success", "Documents updated successfully");
                        progress.markStepComplete(stepId);

                    }
                } else {
                    const response = await EmployeeOnBoardService.editEmployee(
                        payload,
                        currentId,
                    );
                    if (response?.status) {
                        showToastNotification("success", response.message);
                        progress.markStepComplete(stepId);
                    }
                }
            } else {
                const response = await EmployeeOnBoardService.createEmployee(
                    payload as EmployeeOnBoard,
                );
                if (response?.status) {
                    showToastNotification("success", response.message);
                    progress.markStepComplete(stepId);
                }
            }

            // Navigation
            if (nextTab) {
                setActiveTab(nextTab);
            } else {
                navigate(RoutesLink.superAdminEmplyoeeOnboard);
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    };

    // ── Step handler: validate → collect data → submit ──────────────────
    const handleNextStep = async (
        stepId: string,
        nextTab: string,
        fieldsToValidate: (keyof EmployeeOnBoard)[],
        customDocuments?: CustomDocumentPayload[],
    ) => {
        const isValid = await methods.trigger(fieldsToValidate);
        if (!isValid) return;

        // Collect only this step's field values
        const stepData: Partial<EmployeeOnBoard> = {};
        fieldsToValidate.forEach((field) => {
            (stepData as Record<string, unknown>)[field] = methods.getValues(field);
        });

        // Intercept nextTab for Guest/Level 5 employees:
        let actualNextTab = nextTab;
        if (isGuestOrLevel5) {
            if (nextTab === "tab2") {
                actualNextTab = "tab4"; // Skip Permissions (tab2) and Application (tab3)
            }
        }

        if (currentId) {
            // Edit mode: send only current tab fields
            onStepSubmit(stepData, stepId, actualNextTab || undefined, customDocuments);
        } else if (activeTab === "tab2") {
            // First create: send all data (Basic + Permissions)
            onStepSubmit(methods.getValues(), stepId, actualNextTab || undefined, customDocuments);
        } else {
            // Before employee is created (tab1): just move forward
            progress.markStepComplete(stepId);
            setActiveTab(actualNextTab);
        }
    };

    // ── Client submit (tab3 special case) ────────────────────────────────
    const handleClientSubmit = async (stepId: string) => {
        const isValid = await methods.trigger("clientIds");
        if (!isValid) return;
        const clientIds = methods.getValues("clientIds");
        try {
            setIsLoading(true);

            // Build onboardingProcess with this step
            const updatedProcess = progress.completedSteps.includes(stepId)
                ? [...progress.completedSteps]
                : [...progress.completedSteps, stepId];

            const response = await callApi(
                `${API_ENDPOINT.user}/${currentId}/clients`,
                {
                    method: "POST",
                    data: {
                        clientIds: clientIds,
                        onboardingProcess: updatedProcess,
                    },
                },
            );
            if (response?.status) {
                // Persist the onboardingProcess array to the database via editEmployee
                await EmployeeOnBoardService.editEmployee(
                    { onboardingProcess: updatedProcess },
                    currentId,
                );

                showToastNotification("success", response.message);
                progress.markStepComplete(stepId);
                setActiveTab("tab4"); // Navigate to next tab
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    };

    // ── Tab Access Validation ──────────────────────────────────────────
    // Tabs 3–9 require both Basic Details (tab1) & Permissions (tab2) to be complete
    const arePrerequisitesComplete = Boolean(
        id ||
        (currentId &&
            progress.isStepComplete("basic_details"))
    );

    const isStepDisabled = (tabKey: string): boolean => {
        // Basic Details (tab1) and Permissions (tab2) can be accessed for initial setup
        if (tabKey === "tab1") return false;
        // Remaining tabs (tab3 to tab9) require Basic Details & Permissions to be completed
        return !arePrerequisitesComplete;
    };

    const handleTabChange = (newTab: string) => {
        if (isStepDisabled(newTab)) {
            showToastNotification(
                "warning",
                "Please complete Basic Details and Permissions first to access other sections."
            );
            return;
        }
        setActiveTab(newTab);
    };

    // ── Tab indicator (step-based, not field-based) ──────────────────────
    const renderTabIndicator = (stepId: string, disabled: boolean = false) => {
        if (disabled) {
            return (
                <span
                    className="flex items-center justify-center text-slate-400 opacity-60 shrink-0"
                    title="Locked"
                >
                    <Lock className="w-2.5 h-2.5" />
                </span>
            );
        }
        if (progress.isStepComplete(stepId)) {
            return (
                <span className="flex items-center justify-center bg-emerald-500 text-white rounded-full w-4 h-4 p-0.5 shadow-sm text-[9px] font-bold shrink-0">
                    <Check className="w-2.5 h-2.5 stroke-3" />
                </span>
            );
        }
        return (
            <span className="w-1.5 h-1.5 rounded-full bg-slate-300/80 shrink-0" />
        );
    };

    // ── Props passed to every tab component ──────────────────────────────
    const tabProps = {
        isLoading,
        isProfile,
        empId: currentId || null,
        handleNextStep,
        uploadFile,
        handleClientSubmit,
        loggedInUserId,
        isGuestOrLevel5,
        isVerified,
    };

    return (
        <Layout>
            <div className="space-y-6">
                <MainHeading
                    title={"Onboarding"}
                    isBackButtonVisible={true}
                />
                <Card>
                    <FormProvider {...methods}>
                        <form onSubmit={methods.handleSubmit((data) => onStepSubmit(data, "salary_detail"))}>
                            {/* ── Progress Banner ──────────────────── */}
                            <div className="bg-linear-to-r from-slate-50 via-slate-100/30 to-slate-50 border border-slate-200/80 rounded-xl p-5 shadow-sm mb-6 flex flex-col md:flex-row md:items-center justify-between gap-6 transition-all duration-300">
                                <div className="flex-1 w-full">
                                    <div className="flex justify-between items-center mb-2 gap-2 flex-wrap">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-bold text-slate-800 tracking-wide whitespace-nowrap">Form Completion Status</span>
                                            <span className="text-[10px] uppercase font-bold tracking-wider text-primary bg-primary/10 px-2.5 py-0.5 rounded border border-primary/20">Profile Strength</span>
                                        </div>
                                        {/* Conditional: Verified / Under Process / Percentage */}
                                        {isVerified ? (
                                            <span className="flex items-center gap-1.5 text-xs font-extrabold text-emerald-600 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full shadow-sm">
                                                <ShieldCheck className="w-4 h-4" />
                                                Verification Completed
                                            </span>
                                        ) : progress.percentage === 100 ? (
                                            <span className="flex items-center gap-1.5 text-xs font-extrabold text-amber-600 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full shadow-sm">
                                                <ShieldCheck className="w-4 h-4 text-amber-500" />
                                                Verification is under process
                                            </span>
                                        ) : (
                                            <span className="text-xs font-extrabold text-primary bg-primary/10 border border-primary/20 px-3 py-1 rounded-full shadow-sm hover:scale-105 transition-transform duration-200">
                                                {progress.percentage}% Completed
                                            </span>
                                        )}
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="w-full bg-slate-200/50 h-3 rounded-full overflow-hidden border border-slate-200/40 p-0.5 shadow-inner relative">
                                        <div
                                            className="h-full bg-linear-to-r from-blue-500 via-indigo-500 to-violet-600 rounded-full shadow-[0_1px_3px_rgba(0,0,0,0.1)]"
                                            style={{
                                                width: `${progress.percentage}%`,
                                                transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* ── Tabs ─────────────────────────────── */}
                            <Tabs
                                value={activeTab}
                                onValueChange={handleTabChange}
                                className="w-full"
                            >
                                <div className="relative w-full mb-8 group/tabs-container">
                                    {/* Left Arrow */}
                                    {showLeftArrow && (
                                        <div className="absolute -left-3 sm:left-0 top-0 bottom-0 pl-1.5 pr-10 bg-linear-to-r from-slate-100 via-slate-100/90 to-transparent flex items-center z-10 rounded-l-lg pointer-events-none transition-opacity duration-300">
                                            <button
                                                type="button"
                                                onClick={() => scrollTabs("left")}
                                                className="h-8 w-8 rounded-full bg-white shadow-md border border-slate-200/80 flex items-center justify-center text-slate-700 hover:bg-slate-50 hover:text-primary transition-all duration-200 focus:outline-none pointer-events-auto shadow-slate-300/60 hover:scale-105 active:scale-95"
                                                aria-label="Scroll tabs left"
                                            >
                                                <ChevronLeft className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}

                                    {/* Scrollable Tabs List */}
                                    <div
                                        ref={tabsRef}
                                        onScroll={checkScrollButtons}
                                        className="w-full overflow-x-auto overflow-y-hidden scroll-smooth scroll-bar-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] scrollbar-none"
                                    >
                                        <TabsList className="bg-slate-100/80 flex py-5 flex-nowrap h-fit gap-2 rounded-lg border border-slate-200/60 shadow-inner items-center min-w-max justify-start w-full">
                                            {filteredSteps?.map((step) => {
                                                const disabled = isStepDisabled(step.tabKey);
                                                return (
                                                    <TabsTrigger
                                                        key={step?.tabKey}
                                                        value={step?.tabKey}
                                                        disabled={disabled}
                                                        className={`${TAB_TRIGGER_CLASS} ${disabled
                                                            ? "opacity-45 cursor-not-allowed pointer-events-none text-slate-400 select-none"
                                                            : ""
                                                            }`}
                                                        title={
                                                            disabled
                                                                ? "Complete Basic Details and Permissions first to access this section"
                                                                : step.label
                                                        }
                                                    >
                                                        <step.icon className="w-4 h-4" />
                                                        {step.label}
                                                        {renderTabIndicator(step.stepId, disabled)}
                                                    </TabsTrigger>
                                                );
                                            })}
                                        </TabsList>
                                    </div>

                                    {/* Right Arrow */}
                                    {showRightArrow && (
                                        <div className="absolute -right-3 sm:right-0 top-0 bottom-0 pr-1.5 pl-10 bg-linear-to-l from-slate-100 via-slate-100/90 to-transparent flex items-center z-10 rounded-r-lg pointer-events-none transition-opacity duration-300">
                                            <button
                                                type="button"
                                                onClick={() => scrollTabs("right")}
                                                className="h-8 w-8 rounded-full bg-white shadow-md border border-slate-200/80 flex items-center justify-center text-slate-700 hover:bg-slate-50 hover:text-primary transition-all duration-200 focus:outline-none pointer-events-auto shadow-slate-300/60 hover:scale-105 active:scale-95 animate-pulse hover:animate-none"
                                                aria-label="Scroll tabs right"
                                            >
                                                <ChevronRight className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* ── Tab Content (dynamic wrapped in disabled fieldset) ────────── */}
                                <fieldset disabled={isVerified} className={isVerified ? "opacity-80" : ""}>
                                    {filteredSteps?.map((step) => {
                                        const Component = TAB_CONTENT_MAP[step.tabKey];
                                        return (
                                            <TabsContent key={step.tabKey} value={step.tabKey}>
                                                <Component {...tabProps} />
                                            </TabsContent>
                                        );
                                    })}
                                </fieldset>
                            </Tabs>
                        </form>
                    </FormProvider>
                </Card>
            </div>
        </Layout>
    );
};

export default OnBoard;
